import { Search, Bell, User, LogOut, Settings, Building2, AlertTriangle, FileCheck, Clock, Info, CheckCircle2, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth';
import { useCommandStore } from '@/stores/command.store';
import { Logo } from '@/components/ui/logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useUnreadNotificationCount,
  useInAppNotifications,
  useMarkNotificationRead,
} from '@/hooks/use-notifications';

/**
 * Notification type icon mapping.
 */
const notificationTypeIcon: Record<string, React.ReactNode> = {
  'expiration-warning': <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />,
  'compliance-change': <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />,
  'document-approved': <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />,
  'document-processed': <FileCheck className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />,
  'document-rejected': <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />,
  'document-uploaded': <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />,
  'insured-upload-received': <FileCheck className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />,
  'task-assigned': <Clock className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />,
  'coi-request-sent': <Clock className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />,
};

/**
 * Format a date string to a relative time string (e.g. "2 hours ago").
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Notification bell with real data from the API.
 */
function NotificationBell() {
  const navigate = useNavigate();
  const { data: unreadData } = useUnreadNotificationCount();
  const { data: inAppData } = useInAppNotifications();
  const markRead = useMarkNotificationRead();

  const unreadCount = unreadData?.count ?? 0;
  const notifications = inAppData?.items ?? [];

  const handleMarkAllRead = () => {
    markRead.mutate({ all: true });
  };

  const handleNotificationClick = (notification: { id: string; actionUrl?: string | undefined; read: boolean }) => {
    if (!notification.read) {
      markRead.mutate({ notificationId: notification.id });
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-xs font-normal text-coilive-blue hover:underline"
            >
              Mark all as read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            No notifications yet.
          </div>
        ) : (
          notifications.slice(0, 5).map((notification) => (
            <DropdownMenuItem
              key={notification.id}
              className={cn(
                'flex items-start gap-3 p-3',
                !notification.read && 'bg-blue-50/50'
              )}
              onClick={() => handleNotificationClick(notification)}
            >
              {notificationTypeIcon[notification.type] ?? (
                <Info className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm', !notification.read ? 'font-semibold' : 'font-medium')}>
                  {notification.title}
                </p>
                <p className="text-xs text-gray-500 truncate">{notification.message}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatRelativeTime(notification.createdAt)}
                </p>
              </div>
              {!notification.read && (
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-coilive-blue" />
              )}
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="justify-center text-coilive-blue"
          onClick={() => navigate('/app/notifications')}
        >
          View all notifications
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface HeaderProps {
  clientName?: string;
}

/**
 * Application header component.
 * Contains logo, title, search bar, and user actions.
 */
export function Header({ clientName = 'COI LIVE' }: HeaderProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const openCommandPalette = useCommandStore((state) => state.setOpen);

  const handleSearchClick = () => {
    openCommandPalette(true);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b-4 border-coilive-blue bg-white px-6">
      {/* Left: Logo and Title */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <Logo size="lg" />
          <p className="text-xs text-gray-500">for '{clientName}'</p>
        </div>
      </div>

      {/* Center: Search Bar */}
      <div className="flex-1 max-w-xl mx-8">
        <button
          onClick={handleSearchClick}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border border-gray-200',
            'bg-gray-50 px-4 py-2.5 text-left text-gray-500',
            'hover:border-coilive-blue hover:bg-white transition-colors'
          )}
        >
          <Search className="h-5 w-5" />
          <span className="text-sm">Search jobs, insureds, or run reports...</span>
          <kbd className="ml-auto hidden rounded border bg-white px-2 py-0.5 text-xs text-gray-400 sm:block">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Right: User Actions */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <NotificationBell />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-gray-100 transition-colors">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-700">
                  {user?.name ?? user?.email ?? 'User'}
                </p>
                <p className="text-xs text-gray-500">{user?.tenantId ?? 'Tenant'}</p>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-coilive-blue text-white">
                <User className="h-5 w-5" />
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium">{user?.email ?? 'User'}</p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {user?.tenantId ?? 'No tenant'}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/app/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-red-600 focus:text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
